import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { FeedbackType, ChatMessageFeedback } from '@/types/chatbot';
import { useTranslation } from 'react-i18next';

interface FeedbackButtonsProps {
  messageId: string;
  userMessage: string;
  botMessage: string;
  currentFeedback?: ChatMessageFeedback;
  onFeedbackSubmit: (messageId: string, feedbackType: FeedbackType) => Promise<void>;
  onShowDetailsDialog: (feedbackType: FeedbackType) => void;
  className?: string;
}

export const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  messageId,
  currentFeedback,
  onFeedbackSubmit,
  onShowDetailsDialog,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();

  const handleQuickFeedback = async (feedbackType: FeedbackType) => {
    if (isSubmitting || currentFeedback) return;
    
    setIsSubmitting(true);
    try {
      // Submit feedback immediately
      await onFeedbackSubmit(messageId, feedbackType);
      // Then ask if user wants to add details
      onShowDetailsDialog(feedbackType);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-between mt-3">
      <div className="flex items-center gap-1">
        {/* Quick feedback buttons */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleQuickFeedback('positive')}
          disabled={isSubmitting}
          className={cn(
            "flex items-center gap-1.5 text-xs h-8 px-3 rounded-full transition-all duration-200",
            currentFeedback?.feedback_type === 'positive' 
               ? "bg-green-100 text-green-700 border border-green-200 hover:bg-green-200" 
               : "text-gray-500 hover:text-green-600 hover:bg-green-50 border border-transparent hover:border-green-200"
           )}
         >
           <ThumbsUp className="h-3 w-3" />
           <span className="font-medium">{t('chatFeedback.helpful')}</span>
         </Button>

         <Button
           variant="ghost"
           size="sm"
           onClick={() => handleQuickFeedback('negative')}
           disabled={isSubmitting}
           className={cn(
             "flex items-center gap-1.5 text-xs h-8 px-3 rounded-full transition-all duration-200",
             currentFeedback?.feedback_type === 'negative' 
              ? "bg-red-100 text-red-700 border border-red-200 hover:bg-red-200" 
              : "text-gray-500 hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200"
          )}
        >
          <ThumbsDown className="h-3 w-3" />
          <span className="font-medium">{t('chatFeedback.not_helpful')}</span>
        </Button>


      </div>

      {/* Loading indicator */}
      {isSubmitting && (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <div className="animate-spin rounded-full h-3 w-3 border border-gray-300 border-t-blue-600"></div>
          <span>{t('chatFeedback.submitting')}</span>
        </div>
      )}

      {/* Feedback status */}
      {currentFeedback && !isSubmitting && (
        <div className="text-xs text-gray-500 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded-full">
          {t('chatFeedback.feedback_submitted')}
        </div>
      )}
    </div>
  );
};